import {
  qhIntervalLabel,
  type GateStatus,
} from "../../../lib/intervals";
import type {
  DaMarketPriceSource,
  DaPositionSource,
  GridCell,
  GridDisplayZone,
} from "./contracts";

export const GRID_CHART_EPSILON = 1e-9;

export interface ChartPointSeries {
  id: string;
  label: string;
  color: string;
  values: Array<number | null>;
  strokeWidth?: number;
  strokeDasharray?: string;
  pointRadius?: number;
}

export interface ChartBarSeries {
  id: string;
  label: string;
  color: string;
  values: number[];
  opacity: number;
}

export interface SelectionStatLine {
  label: string;
  quantityMwh: number;
  price: number | null;
  strong?: boolean;
}

export interface SelectionPnlLine {
  label: string;
  value: number;
  strong?: boolean;
}

export interface SelectionStats {
  qhCount: number;
  rangeLabel: string;
  lines: SelectionStatLine[];
  pnlLines: SelectionPnlLine[];
}

export interface GridTotals {
  rows: number;
  daPriceEurWeighted: number | null;
  daMwh: number;
  qhBuyMwh: number;
  qhBuyVwap: number | null;
  qhSellMwh: number;
  qhSellVwap: number | null;
  phBuyMwh: number;
  phBuyVwap: number | null;
  phSellMwh: number;
  phSellVwap: number | null;
  idTotalMwh: number;
  idVwap: number | null;
  netMwh: number;
  netVwap: number | null;
  pnl: number | null;
  bmNetMwh: number;
  bmSettlement: number | null;
  bmPnl: number | null;
  bmPricedRows: number;
  bmMissingRows: number;
  openQhs: number;
  dayIsFlat: boolean;
}

export function daSourceLabel(source: DaPositionSource): string {
  if (source === "schedule") return "schedule";
  if (source === "brmda_trades") return "BRMDA trades";
  return "-";
}

export function daMarketPriceSourceLabel(source: DaMarketPriceSource): string {
  if (source === "schedule") return "schedule";
  if (source === "brmda_trades") return "BRMDA trades";
  if (source === "opcom_dam") return "OPCOM DAM fallback";
  if (source === "mixed") return "schedule/trades + OPCOM";
  return "-";
}

export function effectiveBmMw(row: GridCell, gateStatus: GateStatus): number {
  return gateStatus === "closed" ? row.bmNetMw : 0;
}

export function tooltipPnlLines(
  row: GridCell,
  gateStatus: GateStatus
): Array<{ label: string; value: number; strong: boolean }> {
  const lines: Array<{ label: string; value: number; strong: boolean }> = [];
  const hasDaIdActivity =
    Math.abs(row.daBaseMw) > GRID_CHART_EPSILON ||
    Math.abs(idBuyMw(row)) > GRID_CHART_EPSILON ||
    Math.abs(idSellMw(row)) > GRID_CHART_EPSILON;
  const daIdPnl = hasDaIdActivity ? realizedPnl(row) : null;

  if (daIdPnl !== null) lines.push({ label: "DA+IDM", value: daIdPnl, strong: false });
  if (gateStatus === "closed" && row.bmPnlEur !== null) {
    lines.push({ label: "BM", value: row.bmPnlEur, strong: false });
  }

  if (lines.length > 0) {
    lines.push({
      label: "Total",
      value: lines.reduce((sum, line) => sum + line.value, 0),
      strong: true,
    });
  }

  return lines;
}

export function bmTooltipLines(
  row: GridCell,
  bmMw: number
): Array<{ label: string; price: number | null; strong: boolean }> {
  if (Math.abs(bmMw) > GRID_CHART_EPSILON) {
    return [{ label: "BM", price: row.bmAppliedPriceEur ?? bmFallbackPrice(row), strong: true }];
  }

  if (
    isFiniteNumber(row.bmPositivePriceEur) &&
    isFiniteNumber(row.bmNegativePriceEur) &&
    Math.abs(row.bmPositivePriceEur - row.bmNegativePriceEur) > 1e-6
  ) {
    return [
      { label: "BM +", price: row.bmPositivePriceEur, strong: false },
      { label: "BM -", price: row.bmNegativePriceEur, strong: false },
    ];
  }

  return [{ label: "BM", price: bmFallbackPrice(row), strong: false }];
}

export function bmFallbackPrice(row: GridCell): number | null {
  if (isFiniteNumber(row.bmPositivePriceEur)) return row.bmPositivePriceEur;
  if (isFiniteNumber(row.bmNegativePriceEur)) return row.bmNegativePriceEur;
  return row.bmAppliedPriceEur;
}

type WeightedBucket = {
  quantityMwh: number;
  weightedPrice: number;
  pricedMwh: number;
};

export function buildSelectionStats(
  selectedRows: GridCell[],
  rowStatus: ReadonlyMap<number, GateStatus>,
  zone: GridDisplayZone
): SelectionStats | null {
  if (selectedRows.length === 0) return null;
  const rows = [...selectedRows].sort((a, b) => a.qhIndex - b.qhIndex);

  const da = emptyWeightedBucket();
  const daReference = emptyWeightedBucket();
  const idBuy = emptyWeightedBucket();
  const idSell = emptyWeightedBucket();
  const bmBuy = emptyWeightedBucket();
  const bmSell = emptyWeightedBucket();
  const bmReference = emptyWeightedBucket();
  let bmNetMwh = 0;
  let bmNetWeighted = 0;
  let idNetMwh = 0;
  let idNetWeighted = 0;
  let netMwh = 0;
  let netWeighted = 0;
  let daIdPnl = 0;
  let hasDaIdPnl = false;
  let bmPnl = 0;
  let hasBmPnl = false;

  const addIdSigned = (mw: number, price: number | null) => {
    const mwh = mw * 0.25;
    idNetMwh += mwh;
    if (price !== null && Math.abs(mwh) > GRID_CHART_EPSILON) {
      idNetWeighted += mwh * price;
    }
  };

  for (const row of rows) {
    addWeightedBucket(da, row.daBaseMw, row.daMarketPriceEur);
    addZeroWeightedPrice(daReference, row.daMarketPriceEur);
    addWeightedBucket(idBuy, row.qhBuyMw, row.qhBuyVwap);
    addWeightedBucket(idBuy, row.phBuyMw, row.phBuyVwap);
    addWeightedBucket(idSell, row.qhSellMw, row.qhSellVwap);
    addWeightedBucket(idSell, row.phSellMw, row.phSellVwap);
    addIdSigned(row.qhBuyMw, row.qhBuyVwap);
    addIdSigned(row.phBuyMw, row.phBuyVwap);
    addIdSigned(row.qhSellMw, row.qhSellVwap);
    addIdSigned(row.phSellMw, row.phSellVwap);

    netMwh += row.netMw * 0.25;
    netWeighted += signedWeightedSum(row) * 0.25;

    const gateStatus = rowStatus.get(row.qhIndex) ?? "open";
    const bmMw = effectiveBmMw(row, gateStatus);
    const bmPrice = row.bmAppliedPriceEur ?? bmFallbackPrice(row);
    if (bmMw > GRID_CHART_EPSILON) {
      addWeightedBucket(bmBuy, bmMw, bmPrice);
      bmNetMwh += bmMw * 0.25;
      if (bmPrice !== null) bmNetWeighted += bmMw * 0.25 * bmPrice;
    } else if (bmMw < -GRID_CHART_EPSILON) {
      addWeightedBucket(bmSell, bmMw, bmPrice);
      bmNetMwh += bmMw * 0.25;
      if (bmPrice !== null) bmNetWeighted += bmMw * 0.25 * bmPrice;
    } else {
      addZeroWeightedPrice(bmReference, bmPrice);
    }

    const hasDaIdActivity =
      Math.abs(row.daBaseMw) > GRID_CHART_EPSILON ||
      Math.abs(idBuyMw(row)) > GRID_CHART_EPSILON ||
      Math.abs(idSellMw(row)) > GRID_CHART_EPSILON;
    const rowDaIdPnl = hasDaIdActivity ? realizedPnl(row) : null;
    if (rowDaIdPnl !== null) {
      daIdPnl += rowDaIdPnl;
      hasDaIdPnl = true;
    }
    if (gateStatus === "closed" && row.bmPnlEur !== null) {
      bmPnl += row.bmPnlEur;
      hasBmPnl = true;
    }
  }

  const lines: SelectionStatLine[] = [
    {
      label: "DA",
      quantityMwh: da.quantityMwh,
      price: weightedBucketPrice(da) ?? weightedBucketPrice(daReference),
      strong: true,
    },
  ];
  const hasIdBuy = Math.abs(idBuy.quantityMwh) > GRID_CHART_EPSILON;
  const hasIdSell = Math.abs(idSell.quantityMwh) > GRID_CHART_EPSILON;
  if (hasIdBuy) {
    lines.push({
      label: "IDM Buy",
      quantityMwh: idBuy.quantityMwh,
      price: weightedBucketPrice(idBuy),
      strong: !hasIdSell,
    });
  }
  if (hasIdSell) {
    lines.push({
      label: "IDM Sell",
      quantityMwh: idSell.quantityMwh,
      price: weightedBucketPrice(idSell),
      strong: !hasIdBuy,
    });
  }
  if (hasIdBuy && hasIdSell && Math.abs(idNetMwh) > GRID_CHART_EPSILON) {
    lines.push({
      label: "IDM Net",
      quantityMwh: idNetMwh,
      price: idNetWeighted / idNetMwh,
      strong: true,
    });
  }
  if (Math.abs(netMwh) > GRID_CHART_EPSILON) {
    lines.push({
      label: "NET DA+IDM",
      quantityMwh: netMwh,
      price: netWeighted / netMwh,
      strong: true,
    });
  }
  lines.push(...bmSelectionLines(bmBuy, bmSell, bmNetMwh, bmNetWeighted, bmReference));

  const pnlLines: SelectionPnlLine[] = [];
  if (hasDaIdPnl) pnlLines.push({ label: "DA+IDM", value: daIdPnl });
  if (hasBmPnl) pnlLines.push({ label: "BM", value: bmPnl });
  if (pnlLines.length > 0) {
    pnlLines.push({
      label: "Total",
      value: pnlLines.reduce((sum, line) => sum + line.value, 0),
      strong: true,
    });
  }

  return {
    qhCount: rows.length,
    rangeLabel: selectedRangeLabel(rows, zone),
    lines,
    pnlLines,
  };
}

function emptyWeightedBucket(): WeightedBucket {
  return { quantityMwh: 0, weightedPrice: 0, pricedMwh: 0 };
}

function addWeightedBucket(bucket: WeightedBucket, mw: number, price: number | null): void {
  const mwh = mw * 0.25;
  bucket.quantityMwh += mwh;
  if (price === null || Math.abs(mwh) < GRID_CHART_EPSILON) return;
  bucket.weightedPrice += Math.abs(mwh) * price;
  bucket.pricedMwh += Math.abs(mwh);
}

function addZeroWeightedPrice(bucket: WeightedBucket, price: number | null): void {
  if (price === null) return;
  bucket.weightedPrice += price;
  bucket.pricedMwh += 1;
}

function weightedBucketPrice(bucket: WeightedBucket): number | null {
  return bucket.pricedMwh > GRID_CHART_EPSILON
    ? bucket.weightedPrice / bucket.pricedMwh
    : null;
}

function bmSelectionLines(
  bmBuy: WeightedBucket,
  bmSell: WeightedBucket,
  bmNetMwh: number,
  bmNetWeighted: number,
  bmReference: WeightedBucket
): SelectionStatLine[] {
  const hasBuy = Math.abs(bmBuy.quantityMwh) > GRID_CHART_EPSILON;
  const hasSell = Math.abs(bmSell.quantityMwh) > GRID_CHART_EPSILON;
  if (hasBuy && hasSell) {
    return [
      { label: "BM Buy", quantityMwh: bmBuy.quantityMwh, price: weightedBucketPrice(bmBuy) },
      { label: "BM Sell", quantityMwh: bmSell.quantityMwh, price: weightedBucketPrice(bmSell) },
      {
        label: "BM Net",
        quantityMwh: bmNetMwh,
        price: Math.abs(bmNetMwh) > GRID_CHART_EPSILON ? bmNetWeighted / bmNetMwh : null,
        strong: true,
      },
    ];
  }
  if (hasBuy) {
    return [
      {
        label: "BM Buy",
        quantityMwh: bmBuy.quantityMwh,
        price: weightedBucketPrice(bmBuy),
        strong: true,
      },
    ];
  }
  if (hasSell) {
    return [
      {
        label: "BM Sell",
        quantityMwh: bmSell.quantityMwh,
        price: weightedBucketPrice(bmSell),
        strong: true,
      },
    ];
  }
  return [
    {
      label: "BM",
      quantityMwh: 0,
      price: weightedBucketPrice(bmReference),
      strong: true,
    },
  ];
}

function selectedRangeLabel(rows: GridCell[], zone: GridDisplayZone): string {
  const ranges: Array<{ start: number; end: number }> = [];
  for (const row of rows) {
    const last = ranges[ranges.length - 1];
    if (last && row.qhIndex === last.end + 1) last.end = row.qhIndex;
    else ranges.push({ start: row.qhIndex, end: row.qhIndex });
  }
  return ranges
    .map((range) => {
      const start = qhIntervalLabel(range.start, zone).split("-")[0] ?? "";
      const end = qhIntervalLabel(range.end, zone).split("-")[1]?.split(" ")[0] ?? "";
      const qh = range.start === range.end ? `QH-${range.start}` : `QH-${range.start}-${range.end}`;
      return `${qh} ${start}-${end}`;
    })
    .join(", ");
}

export function buildChartSeries(
  rows: GridCell[],
  idmMode: "net" | "split",
  showBm: boolean,
  rowStatus: ReadonlyMap<number, GateStatus>
): { priceSeries: ChartPointSeries[]; barSeries: ChartBarSeries[] } {
  const priceSeries: ChartPointSeries[] = [
    {
      id: "da-price",
      label: "DA price",
      color: "rgb(var(--accent))",
      values: rows.map((row) => row.daMarketPriceEur),
      strokeWidth: 2.3,
      pointRadius: 1.4,
    },
  ];

  if (idmMode === "net") {
    priceSeries.push({
      id: "id-net-price",
      label: "IDM net",
      color: "rgb(var(--pos-green))",
      values: rows.map(idVwap),
      strokeWidth: 2.2,
      pointRadius: 2,
    });
  } else {
    priceSeries.push(
      {
        id: "id-buy-price",
        label: "IDM buy",
        color: "rgb(var(--pos-green))",
        values: rows.map(idBuyVwap),
        strokeWidth: 2.1,
        pointRadius: 2,
      },
      {
        id: "id-sell-price",
        label: "IDM sell",
        color: "rgb(var(--warn-orange))",
        values: rows.map(idSellVwap),
        strokeWidth: 2.1,
        pointRadius: 2,
      }
    );
  }

  if (showBm) {
    if (bmUsesSingleLine(rows)) {
      priceSeries.push({
        id: "bm-price",
        label: "BM price",
        color: "rgb(var(--neg-red))",
        values: rows.map((row) => row.bmPositivePriceEur ?? row.bmNegativePriceEur),
        strokeWidth: 2.4,
        pointRadius: 1.7,
      });
    } else {
      priceSeries.push(
        {
          id: "bm-pos-price",
          label: "BM +",
          color: "rgb(var(--neg-red))",
          values: rows.map((row) => row.bmPositivePriceEur),
          strokeWidth: 2.2,
          pointRadius: 1.6,
        },
        {
          id: "bm-neg-price",
          label: "BM -",
          color: "rgb(var(--neg-red))",
          values: rows.map((row) => row.bmNegativePriceEur),
          strokeWidth: 2,
          strokeDasharray: "5 4",
          pointRadius: 1.6,
        }
      );
    }
  }

  const barSeries: ChartBarSeries[] = [
    {
      id: "da-mw",
      label: "DA",
      color: "rgb(var(--accent))",
      values: rows.map((row) => row.daBaseMw),
      opacity: 0.34,
    },
  ];

  if (idmMode === "net") {
    barSeries.push({
      id: "id-net-mw",
      label: "IDM",
      color: "rgb(var(--pos-green))",
      values: rows.map(idTotalMw),
      opacity: 0.38,
    });
  } else {
    barSeries.push(
      {
        id: "id-buy-mw",
        label: "IDM buy",
        color: "rgb(var(--pos-green))",
        values: rows.map(idBuyMw),
        opacity: 0.38,
      },
      {
        id: "id-sell-mw",
        label: "IDM sell",
        color: "rgb(var(--warn-orange))",
        values: rows.map(idSellMw),
        opacity: 0.4,
      }
    );
  }

  barSeries.push({
    id: "bm-mw",
    label: "BM",
    color: "rgb(var(--neg-red))",
    values: rows.map((row) => effectiveBmMw(row, rowStatus.get(row.qhIndex) ?? "open")),
    opacity: 0.4,
  });

  return { priceSeries, barSeries };
}

export function idBuyMw(row: GridCell): number {
  return row.qhBuyMw + row.phBuyMw;
}

export function idSellMw(row: GridCell): number {
  return row.qhSellMw + row.phSellMw;
}

export function idBuyVwap(row: GridCell): number | null {
  return grossVwap([
    [row.qhBuyMw, row.qhBuyVwap],
    [row.phBuyMw, row.phBuyVwap],
  ]);
}

export function idSellVwap(row: GridCell): number | null {
  return grossVwap([
    [row.qhSellMw, row.qhSellVwap],
    [row.phSellMw, row.phSellVwap],
  ]);
}

function grossVwap(buckets: Array<[number, number | null]>): number | null {
  let weighted = 0;
  let volume = 0;
  for (const [mw, price] of buckets) {
    if (price === null || Math.abs(mw) < GRID_CHART_EPSILON) continue;
    weighted += Math.abs(mw) * price;
    volume += Math.abs(mw);
  }
  return volume > GRID_CHART_EPSILON ? weighted / volume : null;
}

function bmUsesSingleLine(rows: GridCell[]): boolean {
  for (const row of rows) {
    if (!isFiniteNumber(row.bmPositivePriceEur) || !isFiniteNumber(row.bmNegativePriceEur)) {
      continue;
    }
    if (Math.abs(row.bmPositivePriceEur - row.bmNegativePriceEur) > 1e-6) return false;
  }
  return true;
}

export function idTotalMw(row: GridCell): number {
  return row.qhBuyMw + row.qhSellMw + row.phBuyMw + row.phSellMw;
}

export function idVwap(row: GridCell): number | null {
  const buckets: Array<[number, number | null]> = [
    [row.qhBuyMw, row.qhBuyVwap],
    [row.qhSellMw, row.qhSellVwap],
    [row.phBuyMw, row.phBuyVwap],
    [row.phSellMw, row.phSellVwap],
  ];
  let weighted = 0;
  let mw = 0;
  for (const [quantityMw, vwap] of buckets) {
    if (vwap === null || quantityMw === 0) continue;
    weighted += quantityMw * vwap;
    mw += quantityMw;
  }
  return Math.abs(mw) > GRID_CHART_EPSILON ? weighted / mw : null;
}

export function signedWeightedSum(row: GridCell): number {
  return (
    row.daBaseMw * row.daPriceEur +
    (row.qhBuyVwap !== null ? row.qhBuyMw * row.qhBuyVwap : 0) +
    (row.qhSellVwap !== null ? row.qhSellMw * row.qhSellVwap : 0) +
    (row.phBuyVwap !== null ? row.phBuyMw * row.phBuyVwap : 0) +
    (row.phSellVwap !== null ? row.phSellMw * row.phSellVwap : 0)
  );
}

export function netVwap(row: GridCell): number | null {
  if (Math.abs(row.netMw) < GRID_CHART_EPSILON) return null;
  return signedWeightedSum(row) / row.netMw;
}

export function realizedPnl(row: GridCell): number | null {
  if (Math.abs(row.netMw) > GRID_CHART_EPSILON) return null;
  return -0.25 * signedWeightedSum(row);
}

export function bmSpread(row: GridCell): number | null {
  if (row.bmAppliedPriceEur === null || row.bmPositionPriceEur === null) return null;
  return row.bmAppliedPriceEur - row.bmPositionPriceEur;
}

export function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export function paddedRange(values: number[]): { min: number; max: number } {
  if (values.length === 0) return { min: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (Math.abs(max - min) < GRID_CHART_EPSILON) {
    const pad = Math.max(Math.abs(max) * 0.08, 1);
    return { min: min - pad, max: max + pad };
  }
  const pad = (max - min) * 0.08;
  return { min: min - pad, max: max + pad };
}

export function paddedAbs(value: number): number {
  return Math.max(1, value * 1.12);
}

export function makeTicks(min: number, max: number, count: number): number[] {
  if (count <= 1) return [min];
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, index) => min + index * step);
}

export function makeXTicks(
  rows: GridCell[],
  zone: GridDisplayZone
): Array<{ idx: number; label: string }> {
  if (rows.length === 0) return [];
  const wanted = [0, 15, 31, 47, 63, 79, rows.length - 1];
  const unique = [...new Set(wanted.filter((index) => index >= 0 && index < rows.length))];
  return unique.flatMap((index) => {
    const row = rows[index];
    if (!row) return [];
    return {
      idx: index,
      label: qhIntervalLabel(row.qhIndex, zone).split("-")[0] ?? `QH-${row.qhIndex}`,
    };
  });
}

export function lineSegments(
  values: Array<number | null>,
  xForIndex: (index: number) => number,
  yForValue: (value: number) => number
): string[] {
  const segments: string[] = [];
  let path = "";
  values.forEach((value, index) => {
    if (!isFiniteNumber(value)) {
      if (path) segments.push(path);
      path = "";
      return;
    }
    const x = xForIndex(index);
    const y = yForValue(value);
    path += path
      ? ` L ${x.toFixed(2)} ${y.toFixed(2)}`
      : `M ${x.toFixed(2)} ${y.toFixed(2)}`;
  });
  if (path) segments.push(path);
  return segments;
}

export function computeTotals(rows: GridCell[]): GridTotals {
  const totals: GridTotals = {
    rows: rows.length,
    daPriceEurWeighted: null,
    daMwh: 0,
    qhBuyMwh: 0,
    qhBuyVwap: null,
    qhSellMwh: 0,
    qhSellVwap: null,
    phBuyMwh: 0,
    phBuyVwap: null,
    phSellMwh: 0,
    phSellVwap: null,
    idTotalMwh: 0,
    idVwap: null,
    netMwh: 0,
    netVwap: null,
    pnl: 0,
    bmNetMwh: 0,
    bmSettlement: 0,
    bmPnl: 0,
    bmPricedRows: 0,
    bmMissingRows: 0,
    openQhs: 0,
    dayIsFlat: false,
  };

  let daWeighted = 0;
  let daAbs = 0;
  let qhBuyWeighted = 0;
  let qhBuyAbs = 0;
  let qhSellWeighted = 0;
  let qhSellAbs = 0;
  let phBuyWeighted = 0;
  let phBuyAbs = 0;
  let phSellWeighted = 0;
  let phSellAbs = 0;
  let idSignedWeighted = 0;
  let idSignedMw = 0;
  let netSignedWeighted = 0;

  for (const row of rows) {
    totals.daMwh += row.daBaseMw * 0.25;
    if (row.daBaseMw !== 0) {
      daWeighted += Math.abs(row.daBaseMw) * row.daPriceEur;
      daAbs += Math.abs(row.daBaseMw);
    }

    const pushBucket = (
      mw: number,
      vwap: number | null,
      side: "qhBuy" | "qhSell" | "phBuy" | "phSell"
    ) => {
      const mwh = mw * 0.25;
      const absMwh = Math.abs(mwh);
      if (side === "qhBuy") {
        totals.qhBuyMwh += mwh;
        if (vwap !== null) {
          qhBuyWeighted += absMwh * vwap;
          qhBuyAbs += absMwh;
        }
      } else if (side === "qhSell") {
        totals.qhSellMwh += mwh;
        if (vwap !== null) {
          qhSellWeighted += absMwh * vwap;
          qhSellAbs += absMwh;
        }
      } else if (side === "phBuy") {
        totals.phBuyMwh += mwh;
        if (vwap !== null) {
          phBuyWeighted += absMwh * vwap;
          phBuyAbs += absMwh;
        }
      } else {
        totals.phSellMwh += mwh;
        if (vwap !== null) {
          phSellWeighted += absMwh * vwap;
          phSellAbs += absMwh;
        }
      }
      if (vwap !== null && mwh !== 0) {
        idSignedWeighted += mwh * vwap;
        idSignedMw += mwh;
      }
      totals.idTotalMwh += mwh;
    };

    pushBucket(row.qhBuyMw, row.qhBuyVwap, "qhBuy");
    pushBucket(row.qhSellMw, row.qhSellVwap, "qhSell");
    pushBucket(row.phBuyMw, row.phBuyVwap, "phBuy");
    pushBucket(row.phSellMw, row.phSellVwap, "phSell");

    totals.netMwh += row.netMw * 0.25;
    totals.bmNetMwh += row.bmNetMwh;
    if (row.bmSettlementEur !== null) {
      (totals.bmSettlement as number) += row.bmSettlementEur;
      totals.bmPricedRows += 1;
    } else if (Math.abs(row.bmNetMw) > GRID_CHART_EPSILON) {
      totals.bmMissingRows += 1;
    }
    if (row.bmPnlEur !== null) (totals.bmPnl as number) += row.bmPnlEur;

    const signedWeightedEur = signedWeightedSum(row);
    netSignedWeighted += signedWeightedEur * 0.25;
    if (Math.abs(row.netMw) < GRID_CHART_EPSILON) {
      (totals.pnl as number) += -0.25 * signedWeightedEur;
    } else {
      totals.openQhs += 1;
    }
  }

  totals.dayIsFlat = Math.abs(totals.netMwh) < 1e-6;
  totals.daPriceEurWeighted = daAbs > 0 ? daWeighted / daAbs : null;
  totals.qhBuyVwap = qhBuyAbs > 0 ? qhBuyWeighted / qhBuyAbs : null;
  totals.qhSellVwap = qhSellAbs > 0 ? qhSellWeighted / qhSellAbs : null;
  totals.phBuyVwap = phBuyAbs > 0 ? phBuyWeighted / phBuyAbs : null;
  totals.phSellVwap = phSellAbs > 0 ? phSellWeighted / phSellAbs : null;
  totals.idVwap =
    Math.abs(idSignedMw) > GRID_CHART_EPSILON ? idSignedWeighted / idSignedMw : null;
  totals.netVwap =
    Math.abs(totals.netMwh) > GRID_CHART_EPSILON
      ? netSignedWeighted / totals.netMwh
      : null;
  if (totals.bmPricedRows === 0 && totals.bmMissingRows > 0) {
    totals.bmSettlement = null;
    totals.bmPnl = null;
  }

  return totals;
}
